/**
 * NotificationToastHost — slide-in toaster for newly-arrived in-app notifications.
 *
 * Mounted at the top-level layout so the toast follows the user across modules.
 * When the unread-count poller (useUnreadNotifications) sees the count rise, we
 * fetch the full list once, find what's new since last check, and queue calm
 * slide-ins sequenced 500ms apart. Clicking the toast body opens the linked
 * app; the toast auto-dismisses after 8s or on the close button.
 *
 * SOC 2: the toast body shows only the server-supplied title + body — both
 * of which are written by the source module's authorization-aware code path
 * (e.g. Mandate, where the agent enforces no-PHI on the write side). This
 * component does no additional reads.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import useUnreadNotifications from '../lib/useUnreadNotifications';

const API = process.env.REACT_APP_API_URL || '';
const TOAST_TTL_MS = 8_000;
const TOAST_STAGGER_MS = 500;

// Map link_app → SPA app key used by AppSidebar / Home in App.js.
// 'memo' is an external host (Mandate) so we navigate the whole tab.
function urlForLinkApp(linkApp, linkId) {
  if (!linkApp) return null;
  // Memo notifications open the native Precision Mandate module in Core.
  if (linkApp === 'memo' || linkApp === 'mandate_native') {
    return linkId
      ? `/app?app=mandate_native&mndMemo=${encodeURIComponent(linkId)}`
      : '/app?app=mandate_native';
  }
  // Internal SPA app: send to /app?app=<linkApp>
  return `/app?app=${encodeURIComponent(linkApp)}`;
}

export default function NotificationToastHost() {
  const { unreadCount, refresh } = useUnreadNotifications();
  const [toasts, setToasts] = useState([]);
  // The highest notification.created_at we've already toasted. Anything older
  // is treated as "the user has seen this before" so a fresh login doesn't
  // dump 50 backlog toasts on screen.
  const seenCursorRef = useRef(null);
  // Track previous unread count so we only fetch the list when count rises.
  const lastUnreadRef = useRef(null);
  const queueRef = useRef([]);
  const queueTimerRef = useRef(null);

  // Drain queued toasts at TOAST_STAGGER_MS intervals so two-finishes-at-once
  // doesn't stack visually.
  const drainQueue = useCallback(() => {
    if (queueTimerRef.current) return;
    const tick = () => {
      const next = queueRef.current.shift();
      if (!next) {
        clearInterval(queueTimerRef.current);
        queueTimerRef.current = null;
        return;
      }
      setToasts(prev => [...prev, next]);
    };
    // First one fires immediately, then every 500ms while queue has items.
    tick();
    queueTimerRef.current = setInterval(tick, TOAST_STAGGER_MS);
  }, []);

  // When unreadCount rises, fetch the list and queue toasts for anything newer
  // than the seen cursor. First render seeds the cursor without toasting so we
  // don't blast the user with their backlog.
  useEffect(() => {
    if (lastUnreadRef.current === null) {
      // First poll — seed the seen cursor from the current list without showing toasts.
      lastUnreadRef.current = unreadCount;
      (async () => {
        try {
          const res = await fetch(`${API}/api/notifications`, { credentials: 'include' });
          const data = await res.json();
          if (data?.success && Array.isArray(data.notifications) && data.notifications.length) {
            // notifications is ordered DESC — first row is the newest.
            seenCursorRef.current = data.notifications[0].created_at;
          } else {
            seenCursorRef.current = new Date().toISOString();
          }
        } catch {
          seenCursorRef.current = new Date().toISOString();
        }
      })();
      return;
    }
    if (unreadCount > lastUnreadRef.current) {
      (async () => {
        try {
          const res = await fetch(`${API}/api/notifications`, { credentials: 'include' });
          const data = await res.json();
          if (!data?.success || !Array.isArray(data.notifications)) return;
          const cursor = seenCursorRef.current;
          // Newest first → keep only items strictly newer than the cursor.
          const fresh = data.notifications.filter(n =>
            !cursor || new Date(n.created_at).getTime() > new Date(cursor).getTime()
          );
          if (fresh.length === 0) return;
          // Reverse so oldest-of-the-fresh shows first; user reads top-down.
          const ordered = [...fresh].reverse();
          for (const n of ordered) {
            queueRef.current.push({
              id: n.id,
              type: n.type,
              title: n.title,
              body: n.body,
              link_app: n.link_app,
              link_id: n.link_id,
              created_at: n.created_at,
            });
          }
          seenCursorRef.current = fresh[0].created_at;
          drainQueue();
        } catch {
          /* silent */
        }
      })();
    }
    lastUnreadRef.current = unreadCount;
  }, [unreadCount, drainQueue]);

  // Auto-dismiss each toast after TOAST_TTL_MS.
  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map(t =>
      setTimeout(() => {
        setToasts(prev => prev.filter(x => x.id !== t.id));
      }, TOAST_TTL_MS)
    );
    return () => { timers.forEach(clearTimeout); };
  }, [toasts]);

  const dismiss = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const markRead = async (id) => {
    try {
      await fetch(`${API}/api/notifications/${id}/read`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      refresh();
    } catch { /* silent */ }
  };

  const handleOpen = (t) => {
    markRead(t.id);
    const href = urlForLinkApp(t.link_app, t.link_id);
    dismiss(t.id);
    if (!href) return;
    if (href.startsWith('http')) {
      window.location.assign(href);
    } else {
      window.location.href = href;
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-24 right-6 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none"
      aria-live="polite"
    >
      <style>{`
        @keyframes slideInRight { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
      {toasts.map(t => {
        const isFailure = t.type === 'memo_analysis_failed';
        return (
          <div
            key={t.id}
            role="status"
            onClick={() => handleOpen(t)}
            className={`pointer-events-auto cursor-pointer rounded-xl border bg-white shadow-lg p-4 pr-9 transition-colors hover:bg-slate-50 ${
              isFailure ? 'border-red-200' : 'border-slate-200'
            }`}
            style={{ animation: 'slideInRight .22s cubic-bezier(.16,1,.3,1)' }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); dismiss(t.id); }}
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 text-lg leading-none w-6 h-6 flex items-center justify-center rounded-md hover:bg-slate-100"
              aria-label="Dismiss notification"
            >
              {'×'}
            </button>
            <p className={`text-sm font-medium ${isFailure ? 'text-red-700' : 'text-slate-800'}`}>
              {t.title}
            </p>
            {t.body && (
              <p className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-3">
                {t.body}
              </p>
            )}
            {t.link_app && (
              <p className="text-[11px] font-medium text-slate-500 mt-2">
                {t.link_app === 'memo' ? 'Open in Mandate' : `Open ${t.link_app}`}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
